import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, FileText, AlertCircle } from "lucide-react";

interface TermsProps {
  onBackToLanding: () => void;
}

export function Terms({ onBackToLanding }: TermsProps) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBackToLanding}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Button>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="text-2xl font-semibold">Terms of Service</h1>
              <p className="text-sm text-muted-foreground">
                Our terms and conditions for using Decisionr.xyz
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-lg mb-4">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Last updated: January 1, 2025</span>
          </div>
          <h2 className="text-3xl font-medium mb-4">Terms of Service</h2>
          <p className="text-lg text-muted-foreground">
            Please read these terms carefully before using Decisionr.xyz.
          </p>
        </div>

        <div className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Acceptance of Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                By accessing or using Decisionr.xyz, you agree to be bound by these Terms of Service. 
                If you disagree with any part of these terms, you may not access the service.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Use of Service</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Permitted Use</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Personal decision tracking and analysis</li>
                  <li>• Educational and research purposes</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Prohibited Use</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Illegal activities or violation of laws</li>
                  <li>• Sharing account credentials with others</li>
                  <li>• Attempting to breach our security measures</li>
                  <li>• Using the service to harm others or spread misinformation</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User Data and Privacy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                You retain ownership of all decision data you input into our platform. We are committed to protecting your privacy as outlined in our Privacy Policy.
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• You own your decision data</li>
                <li>• You can export or delete your data at any time</li>
                <li>• We use data only as described in our Privacy Policy</li>
                <li>• You're responsible for backing up important data</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment and Billing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Subscription Plans</h4>
                <p className="text-muted-foreground">
                  Paid features require a valid subscription. Billing occurs according to your chosen plan (annual or lifetime).
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Refunds</h4>
                <p className="text-muted-foreground">
                  We offer a 30-day money-back guarantee for all paid plans. Refund requests must be made within 30 days of purchase.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Service Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We strive to maintain high availability but cannot guarantee uninterrupted service. We may perform maintenance, 
                updates, or modifications that temporarily affect service availability.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Decisionr.xyz is provided "as is" without warranties. We are not liable for any indirect, incidental, 
                or consequential damages arising from your use of the service. Our liability is limited to the amount 
                you paid for the service in the past 12 months.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We may update these terms periodically. We'll notify you of significant changes via email or through 
                the service. Continued use after changes constitutes acceptance of the new terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• We may suspend or terminate your account if you violate these terms, engage in prohibited activities, or if your account remains inactive for more than 12 months.</li>
                <li>• You may terminate your account at any time by contacting us.</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>User Content Responsibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• You are solely responsible for the content you input into Decisionr.xyz.</li>
                <li>• We do not monitor or endorse any decisions, outcomes, or statements made within your account.</li>
                <li>• You agree not to submit unlawful, harmful, or defamatory material.</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Indemnification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• You agree to defend, indemnify, and hold harmless Decisionr.xyz and its affiliates from any claims, damages, liabilities, and expenses arising from your use of the service, your content, or your violation of these terms.</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Age Requirements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• You must be at least 16 years old to use Decisionr.xyz.</li>
                <li>• If you are under the age of majority in your jurisdiction, you must have parental or guardian consent.</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• These terms are governed by the laws of the State of Illinois, USA, without regard to conflict of law principles.</li>
                <li>• Any disputes will be resolved exclusively in the state or federal courts located in Chicago, Illinois.</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• We may retain backup copies of your data for up to 90 days after deletion for legal or operational purposes.</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Service Changes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-1 text-muted-foreground">
                <li>• We may add, remove, or change features at any time without prior notice.</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-muted/30">
            <CardContent className="p-6 text-center">
              <h3 className="font-medium mb-2">Questions about these terms?</h3>
              <p className="text-muted-foreground mb-4">
                Contact us at legal@decisionr.xyz for clarification.
              </p>
              <Button variant="outline">Contact Legal Team</Button>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}